import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { UserIcon, EnvelopeIcon, GlobeAltIcon, BuildingOfficeIcon } from "@heroicons/react/24/solid";
import moment from "moment-timezone"; // 12 Oct 2025

// 14 Oct 2025
import { useCreateNewUserGrowthSparkRequestMutation } from "@/features/userGrowthSparkRequests/userGrowthSparkRequestsApiSlice";


// Step Labels (progress dots)
const steps = ["intro", "profile", "phone", "role", "presence", "prompt", "time", "review","confirm","finish"];

const EtContactConversation = ({ 
  onDone,
  ctaSource, // 25 Nov 2025 
}) => {

  // First Created - 10 Oct 2025
  // Author - Atul Pandey
  // Purpose - Trinity-style conversational intake (local only, API logic to be added later)
  // Updated - 11 Oct 2025 - Added phone. Refined JSX - added UserResponseCard
  // Updated - 12 Oct 2025 - Other role fix
  // Updated - 12 Oct 2025 - Added organization name in case website, LinkedIn url is not provided
  // Updated - 13 Oct 2025 - Fixes for Time segment - i===6 and helper function 3a2
  // Updated - 14 Oct 2025 - Refinements for the final confirmation, edits and finish
  // Updated - 14 Oct 2025 - RTK mutation to create the record in the backend
  // Updated - 25 Nov 2025 - ctaSource refinements

  // 1 -  Local state
  const [i, setI] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [linkedInUrl, setLinkedInUrl] = useState("");
  const [youtubeHandle, setYoutubeHandle] = useState("");
  const [growthPrompt, setGrowthPrompt] = useState("");
  const [week, setWeek] = useState("this"); // this | next
  const [daypart, setDaypart] = useState("am"); // am | mid | pm
  const [requestedDateTimeUTC, setRequestedDateTimeUTC] = useState("");
  const [consent, setConsent] = useState(true);

  const [typing, setTyping] = useState(false); // 11 Oct 2025 - for simulating thinking while typing
  const [otherRole, setOtherRole] = useState(""); // 12 Oct 2025 - Other role fix
  const [organizationName, setOrganizationName] = useState(""); // 12 Oct 2025
  const [showOrgField, setShowOrgField] = useState(false); // 12 Oct 2025
  const [suppressAutoPrefill, setSuppressAutoPrefill] = useState(false); // 12 Oct 2025

  const [showCaptured, setShowCaptured] = useState(true) // 14 Oct 2025

  const [isSubmitting, setIsSubmitting] = useState(false); // 15 Oct 2025


  // 2a1 - RTK mutation
  const [createNewUserGrowthSparkRequest, { isLoading, isSuccess, isError, error }] =
  useCreateNewUserGrowthSparkRequestMutation();

    
  // 2a2 - Scroll Ref
  const scrollRef = useRef(null);
  const tz = useMemo(() => moment.tz.guess(), []);

  // 2b1 - Week Selection
  useEffect(() => {
    if (suppressAutoPrefill) return;  // ⬅️ don’t override manual edits
    if (!week) return;

    let localMoment = moment().tz(tz);

    // --- Determine default hour based on daypart ---
    // Morning: till 12:00 PM → 11 AM
    // Midday: 12:00 PM - 3:59 PM → 1 PM
    // Late: after 4:00 PM → 4 PM
    let defaultHour = 11; // morning
    if (daypart === "mid") defaultHour = 13; // 1 PM midday (12:00 PM - 3:59 PM)
    if (daypart === "pm") defaultHour = 16; // 4 PM late (after 4:00 PM)

    if (week === "this") {
      // If Sunday and after 8 PM → skip to Tuesday to avoid past times
      const isSunday = localMoment.isoWeekday() === 7;
      const isLateSunday = isSunday && localMoment.hour() >= 20;

      if (isLateSunday) {
        localMoment = localMoment.add(2, "day"); // Tuesday
      } else {
        localMoment = localMoment.add(1, "day"); // tomorrow
      }

      localMoment.hour(defaultHour).minute(0).second(0);
    } else if (week === "next") {
      // If today is Sunday, skip one full week ahead → next-next Monday
      const isSunday = localMoment.isoWeekday() === 7;
      const daysUntilMonday = isSunday
        ? 8 // skip immediate Monday
        : (8 - localMoment.isoWeekday()) % 7 || 7;

      localMoment = localMoment
        .add(daysUntilMonday, "day")
        .hour(defaultHour)
        .minute(0)
        .second(0);
    } else {
      // later — user picks manually
      setRequestedDateTimeUTC("");
      return;
    }

    // datetime-local expects local time, not UTC
    setRequestedDateTimeUTC(localMoment.format("YYYY-MM-DDTHH:mm"));
  }, [week, tz, daypart, suppressAutoPrefill]);

  // 2b2 - Scroll Control
  useEffect(() => {
    const timer = setTimeout(() => {
        if (scrollRef.current) {
        scrollRef.current.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior: "smooth",
        });
        }
    }, 250); // small delay for motion animations to settle

    return () => clearTimeout(timer);
  }, [i]);

  // 2b3 - Derived confirmation text for selected date/time
  const confirmationText = useMemo(() => {
    // if (!requestedDateTimeUTC || week === "later") return "";
    if (!requestedDateTimeUTC) return null;
    try {
      // const localMoment = moment(requestedDateTimeUTC).tz(tz);
      const localMoment = moment.tz(requestedDateTimeUTC, tz);
      const derived = deriveWeek(localMoment, tz); // ⬅️ robust label

      const dayName = localMoment.format("dddd");
      const dateFormatted = localMoment.format("MMM D");
      const timeFormatted = localMoment.format("h:mm A");
      // const weekLabel = week === "this" ? "this" : "next";
      const weekLabel = derived === "this" ? "this" : derived === "next" ? "next" : "later";

      return (
       <>
         Got it — I’ve penciled in{" "}
         <span className="text-orange-400 font-semibold">
           {weekLabel !== "later" ? `${weekLabel} ` : ""}
           {dayName}, {dateFormatted} at {timeFormatted}
         </span>
         . Adjust if needed.
       </>
     );
    } catch {
      // return "";
      return null
    }
  // }, [requestedDateTimeUTC, week, tz]);
  }, [requestedDateTimeUTC, tz]);

  // 2b4 - Format meeting date
  const formatMeeting = (date) => {

    if (!date) return null;
    const localMoment = moment.tz(date, tz);
    const derived = deriveWeek(localMoment, tz); // ⬅️ robust label

    const dayName = localMoment.format("dddd");
    const dateFormatted = localMoment.format("MMM D");
    const timeFormatted = localMoment.format("h:mm A");
    const weekLabel = derived === "this" ? "this" : derived === "next" ? "next" : "later";

    return (
       <>
         <span className="text-orange-400 font-semibold">
           {weekLabel !== "later" ? `${weekLabel} ` : ""}
           {dayName}, {dateFormatted} at {timeFormatted}
         </span>
       </>
    )
  }

  // 3a1 - Helper to classify week from a moment
  function deriveWeek(m, tz) {
    const base = moment.tz(tz);
    const startThis = base.clone().startOf("isoWeek");                 // Mon
    const endThis = startThis.clone().add(6, "days").endOf("day");     // Sun
    const startNext = startThis.clone().add(7, "days");                // next Mon
    const endNext = startNext.clone().add(6, "days").endOf("day");     // next Sun
    if (m.isBetween(startThis, endThis, null, "[]")) return "this";
    if (m.isBetween(startNext, endNext, null, "[]")) return "next";
    return "later";
  }

  // 3a2 - Helper to classify daypart from a moment - 13 Oct 2025
  function deriveDaypart(m) {
    const hour = m.hour();
    // Morning: 00:00 - 11:59 (till 12:00 PM)
    if (hour < 12) return "am";
    // Midday: 12:00 - 15:59 (12:00 PM - 3:59 PM)
    if (hour >= 12 && hour < 16) return "mid";
    // Late: 16:00+ (after 4:00 PM)
    return "pm";
  }

  // 3a3 - Handle Next
  function handleNext() {
    if (i < steps.length - 1) {
      setTyping(true);
      const nextStep = steps[i + 1];
      const delay = nextStep === "time" || nextStep === "finish" ? 1200 : 800;
      setTimeout(() => {
        setTyping(false);
        setI((v) => v + 1);

        // ✅ Hide captured details as we move into the "finish" step (step 9)
        if (nextStep === "finish") {
        setShowCaptured(false);
      }
      }, delay);
    } 
  }

  // 3b - Handle Finish
  const handleFinish = async () => {

    // 14 Oct 2025
    const payload = {
      name,
      email,
      phone,
      role,
      organizationName,
      websiteUrl,
      linkedInUrl,
      youtubeHandle,
      growthPrompt,
      requestedDateTimeUTC,
      daypart,
      week,
      timezone: tz,
      consent,
      ctaSource, // 25 Nov 2025
    };

    console.log('📤 Submitting Growth Spark payload:', payload);
    
    // 15 Oct 2025
    setIsSubmitting(true); 
    setTyping(true);

    try {
      await createNewUserGrowthSparkRequest(payload).unwrap();

      // ✅ 15 Oct 2025 - Once done, show final bubble
    setTyping(false);
    setI((v) => v + 1);
    } catch (err) {
      console.error('❌ Error submitting Growth Spark:', err);
      // Optional: show UI error message
    } finally {
      // ✅ Always reset loading spinner, even if there’s an error
      setIsSubmitting(false);
    }

    // console.log("Collected Contact Lead:", {
    //   name,
    //   email,
    //   phone,
    //   role,
    //   websiteUrl,
    //   linkedInUrl,
    //   youtubeHandle,
    //   growthPrompt,
    //   week,
    //   daypart,
    //   requestedDateTimeUTC,
    //   consent,
    //   timezone: tz,
    // });
    
    // 11 Oct 2025
    // onDone();
    // show thank-you message before closing
    
    
    // setTimeout(() => {
    //     setTyping(false);
    //     setI((v) => v + 1); // trigger final bot bubble
    // }, 600);
  }

  // 11 Oct 2025 - on HandleFinish - last step
  useEffect(() => {
    if (i > 9) {
        const timer = setTimeout(() => {
        onDone();
        }, 2500); // 2.5 s pause
        return () => clearTimeout(timer);
    }
  }, [i]);

  // 3c - isValidName
  function isValidName(value) {
    return /^[a-zA-Z\s'-]{2,}$/.test(value.trim());
  }

  // 3d - Email validation helper
  function isValidEmail(value) {
    return /^[\w._%+-]+@[\w.-]+\.[a-zA-Z]{2,}$/i.test(value.trim());
  }

  // 3e - Phone validation helper
  function isValidPhone(value) {
    const cleaned = value.replace(/[^0-9]/g, "");
    return cleaned.length >= 8 && cleaned.length <= 15;
  }

  // 3f - Role validation helper
  function isValidRole(value) {
    return value && value.trim().length > 1;
  }

  // 3g - Growth prompt validation - 12 Oct 2025
  function isValidPrompt(value) {
    return value.trim().length >= 10; // at least 10 characters
  }


  // 3h - isValidStep
  function isStepValid() {
    switch (steps[i]) {
        case "profile":
          return isValidName(name) && isValidEmail(email);
        case "phone":
          return isValidPhone(phone);
        case "role":
        return isValidRole(role === "Other" ? otherRole : role); // 12 Oct 2025
        // 12 Oct 2025 — two-stage validation: allow first click to trigger showOrgField; enforce only after reveal
        case "presence":  
          if (showOrgField) {
            return (
                websiteUrl.trim() !== "" ||
                linkedInUrl.trim() !== "" ||
                organizationName.trim() !== ""
            );
          }
          return true; // before org field shown, permit click to trigger reveal
        case "prompt":
          return isValidPrompt(growthPrompt);
        case "time":
          return (week && daypart) || requestedDateTimeUTC;
        default:
          return true;
    }
  }

  return (
    <div className="p-6">

      {isError && (
        <div className="mt-4 text-sm text-red-400">
          Oops! Something went wrong while submitting. Please try again.
        </div>
      )}

      {/* Step A: Progress dots */}
      <div className="flex justify-center gap-2 mb-4">
        {steps.map((_, idx) => (
          <div
            key={idx}
            className={`h-1.5 w-6 sm:w-8 rounded-full ${idx <= i ? "bg-orange-500" : "bg-white/10"}`}
          />
        ))}
      </div>

      {/* Step B: Chat area */}
      <div ref={scrollRef} className="space-y-6 max-h-[60vh] overflow-y-auto pr-1">
        {showCaptured && i >= 0 && <BotBubble>Hello!👋 I’m <strong>Trinity</strong>. Want a quick 60-second check-in so I can craft a mini <em>Growth Spark</em> for you?</BotBubble>}

        {typing && (
            <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-[85%]"
            >
                <div className="flex gap-1 bg-white/5 border border-white/10 rounded-2xl px-4 py-2 text-gray-400">
                    <span className="animate-bounce">•</span>
                    <span className="animate-bounce delay-150">•</span>
                    <span className="animate-bounce delay-300">•</span>
                </div>
            </motion.div>
        )}

        {showCaptured && i >= 1 && !typing && <BotBubble>Great—what's your name and email?</BotBubble>}
        {showCaptured && i === 1 && (
          <UserCard>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="et-input" />
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" type="email" className="et-input" />
            </div>

            {/* 11 Oct 2025 - Inline validation feedback */}
            { name && !isValidName(name) && (
            <p className="text-xs text-red-400 mt-2 text-left">Please enter a valid name (letters only).</p>
            )}

            { email && !isValidEmail(email) && (
            <p className="text-xs text-red-400 mt-1 text-left">Please enter a valid email address.</p>
            )}

            <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
              <input id="consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <label htmlFor="consent">I agree to be contacted and receive my Growth Spark.</label>
            </div>
          </UserCard>
        )}

        {showCaptured && i > 1 &&  name && (
            <UserResponseCard>
                <p>{name}</p>     
            </UserResponseCard>
        )}
        {showCaptured && i > 1 &&  email && (
            <UserResponseCard>
                <p>{email}</p>     
            </UserResponseCard>
        )}

        {showCaptured && i >= 2 && !typing && <BotBubble>Great — what's the best number to reach you?</BotBubble>}
        {showCaptured && i === 2 && (
          <UserCard>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Your phone" type="tel" className="et-input" />
            </div>

            { phone && !isValidPhone(phone) && (
            <p className="text-xs text-red-400 mt-2 text-left">
                Please enter a valid phone number (8–15 digits).
            </p>
            )}
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
              <input id="consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <label htmlFor="consent">I agree to be contacted via phone.</label>
            </div>
          </UserCard>
        )}

        {showCaptured && i > 2 && phone && (
            <UserResponseCard>
                <p>{phone}</p>     
            </UserResponseCard>
        )}

        {showCaptured && i >= 3 && !typing && <BotBubble>Which role best describes you?</BotBubble>}
        {showCaptured && i === 3 && (
          <UserCard>
            <div className="flex flex-wrap gap-2">
              {["Founder/CEO", "COO", "CRO", "Head of Delivery", "Investor", "Other"].map((r) => (
                <button
                    key={r}
                    onClick={() => {
                    if (r === "Other") {
                        setRole("Other");
                        setOtherRole(""); // reset the input field
                    } else {
                        setRole(r);
                        handleNext(); // auto-advance for non-Other
                    }
                    }}
                    className={`et-chip ${role === r ? "et-chip-active" : ""}`}
                >
                    {r}
                </button>
               ))}

            </div>
            
            {role === "Other" && (
            <input
                value={otherRole}
                onChange={(e) => setOtherRole(e.target.value)}
                onKeyDown={(e) => {
                if (e.key === "Enter" && isValidRole(otherRole)) {
                    // normalize the custom role
                    setRole(otherRole.trim());
                    handleNext();
                }
                }}
                placeholder="Type your role"
                className="et-input mt-3"
            />
            )}

            {!isValidRole(role === "Other" ? otherRole : role) && (
            <p className="text-xs text-red-400 mt-2 text-left">
                Please select or enter your role.
            </p>
            )}

          </UserCard>
        )}
        {showCaptured && i > 3 && role && <UserBubble>{role}</UserBubble>}

        {showCaptured && i === 4 && !typing && showOrgField && (
          <BotBubble>
            Got it — if you don’t have a website or LinkedIn handy, just tell me your
            <strong className="text-orange-400"> organization name</strong> instead.
          </BotBubble>
        )}
        {showCaptured && i > 4 && !typing && showOrgField && (
          <BotBubble>
            What's your
            <strong className="text-orange-400"> organization </strong> name?.
          </BotBubble>
        )}


        {showCaptured && i >= 4 && !showOrgField && !typing && <BotBubble>Where can I learn about you? Add any that apply.</BotBubble>}
        {i === 4 && (
          <UserCard>
            {!showOrgField && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder="Website URL" className="et-input" />
              <input value={linkedInUrl} onChange={(e) => setLinkedInUrl(e.target.value)} placeholder="LinkedIn URL" className="et-input" />
              <input value={youtubeHandle} onChange={(e) => setYoutubeHandle(e.target.value)} placeholder="YouTube handle" className="et-input" />
            </div>
            )}

            {/* 12 Oct 2025 — Conditional organization name field */}
            {showOrgField && (
            <input
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="Organization / Company Name"
                className="et-input mt-3"
            />
            )}
            
          </UserCard>
        )}
        

        {showCaptured && i > 4 && websiteUrl && (
            <UserResponseCard>
                <div className="flex justify-center items-center gap-2">
                <GlobeAltIcon className="h-4 w-4" />
                    <p className="font-bold text-white/90">Website:</p>
                </div>
                <p>{websiteUrl}</p>              
            </UserResponseCard>
        )}
        {showCaptured && i > 4 && linkedInUrl && (
            <UserResponseCard>
                <div className="flex justify-center items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                >
                  <path d="M19 0h-14c-2.76 0-5 2.24-5 5v14c0 2.76 
                  2.24 5 5 5h14c2.76 0 5-2.24 
                  5-5v-14c0-2.76-2.24-5-5-5zm-11 
                  19h-3v-10h3v10zm-1.5-11.27c-.97 
                  0-1.75-.79-1.75-1.76s.78-1.75 
                  1.75-1.75 1.75.78 1.75 1.75-.78 
                  1.76-1.75 1.76zm13.5 11.27h-3v-5.6c0-1.34-.03-3.07-1.87-3.07-1.87 
                  0-2.16 1.46-2.16 2.97v5.7h-3v-10h2.89v1.37h.04c.4-.75 
                  1.39-1.54 2.86-1.54 3.06 0 3.63 
                  2.01 3.63 4.63v5.54z" />
                </svg>
                    <p className="font-bold text-white/90">LinkedIn:</p>
                </div>
                <p>{linkedInUrl}</p>              
            </UserResponseCard>
        )}
        {showCaptured && i > 4 && youtubeHandle && (
            <UserResponseCard>
                <div className="flex justify-center items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 576 512"
                  fill="currentColor"
                  className="h-5 w-5"
                >
                  <path d="M549.655 124.083c-6.281-23.65-24.787-42.276-48.284-48.596C456.331 64 288 64 288 64S119.669 
                  64 74.629 75.487c-23.497 6.32-42.003 24.946-48.284 
                  48.596C16 169.216 16 256 16 256s0 86.784 10.345 
                  131.917c6.281 23.65 24.787 42.276 48.284 48.596C119.669 
                  448 288 448 288 448s168.331 0 213.371-11.487c23.497-6.32 
                  42.003-24.946 48.284-48.596C560 342.784 560 256 
                  560 256s0-86.784-10.345-131.917zM232 334.059V177.941L364.118 
                  256 232 334.059z"/>
                </svg>
                    <p className="font-bold text-white/90">YouTube:</p>
                </div>
                <p>{youtubeHandle}</p>              
            </UserResponseCard>
        )}
        {showCaptured && i > 4 && organizationName && (
            <UserResponseCard>
                <div className="flex justify-center items-center gap-2">
                <BuildingOfficeIcon className="h-4 w-4" />
                    <p className="font-bold text-white/90">Organization:</p>
                </div>
                <p>{organizationName}</p>              
            </UserResponseCard>
        )}

        {showCaptured && i >= 5 && !typing && (
          <BotBubble>
            What’s on your mind regarding growth?  
            <span className="block text-sm text-gray-400 mt-1">
              (A short note on what’s working—or what you’d like to shift.)
            </span>
          </BotBubble>
        )}
        {showCaptured && i === 5 && (
          <UserCard>
            <textarea
              value={growthPrompt}
              onChange={(e) => setGrowthPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && isValidPrompt(growthPrompt)) {
                  e.preventDefault();
                  handleNext();
                }
              }}
              rows={3}
              placeholder="e.g., We need a crisper GTM focus and better delivery margins."
              className="et-input"
            />

            {!isValidPrompt(growthPrompt) && growthPrompt.trim() !== "" && (
              <p className="text-xs text-red-400 mt-2 text-left">
                Please share at least a few words (10+ characters) so Trinity can frame your Growth Spark.
              </p>
            )}
          </UserCard>
        )}

        {showCaptured && i > 5 && growthPrompt && (
            <UserResponseCard>
                <p>{growthPrompt}</p>     
            </UserResponseCard>
        )}

        {/* 12 Oct 2025 — Step 6 delay before showing next prompt */}
        {showCaptured && i === 6 && typing && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-[85%]"
          >
            <div className="flex gap-1 bg-white/5 border border-white/10 rounded-2xl px-4 py-2 text-gray-400">
              <span className="animate-bounce">•</span>
              <span className="animate-bounce delay-150">•</span>
              <span className="animate-bounce delay-300">•</span>
            </div>
          </motion.div>
        )}

        {showCaptured && i >= 6 && !typing && (
          <BotBubble>
            When is a good time to connect?
          </BotBubble>
        )}

        {showCaptured && i === 6 && (
          <UserCard>
            <div className="flex flex-wrap gap-2">
              {["this", "next", "later"].map((w) => (
                <button
                  key={w}
                  onClick={() => {
                    // FIX #1: Clear manual edit flag to allow auto-prefill when user clicks a chip
                    setSuppressAutoPrefill(false);
                    setWeek(w);
                    
                    // FIX #2: If clicking away from "later", preserve any valid manual time user entered
                    // Only reset if the datetime is empty or we're moving to "later"
                    if (w === "later" && requestedDateTimeUTC) {
                      // Keep the datetime but don't auto-adjust it
                      return;
                    }
                  }}
                  className={`et-chip ${week === w ? "et-chip-active" : ""}`}
                >
                  {w === "this"
                    ? "This week"
                    : w === "next"
                    ? "Next week"
                    : "Later"}
                </button>
              ))}
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              {["am", "mid", "pm"].map((dp) => (
                <button 
                  key={dp} 
                  onClick={() => {
                    setDaypart(dp);

                    // FIX #3a: For "later" week, preserve the date and only adjust time
                    if (week === "later" && requestedDateTimeUTC) {
                      const currentMoment = moment.tz(requestedDateTimeUTC, tz);
                      if (currentMoment.isValid()) {
                        // Preserve date, only update hour based on daypart
                        let newHour = 11; // morning (till 12:00 PM)
                        if (dp === "mid") newHour = 13; // midday (12:00 PM - 3:59 PM)
                        if (dp === "pm") newHour = 16; // late (after 4:00 PM)
                        
                        currentMoment.hour(newHour).minute(0).second(0);
                        setRequestedDateTimeUTC(currentMoment.format("YYYY-MM-DDTHH:mm"));
                        setSuppressAutoPrefill(true); // Prevent auto-prefill from overriding
                        return;
                      }
                    }

                    // FIX #3b: Allow auto-prefill to adjust time when daypart changes
                    setSuppressAutoPrefill(false);
                  }}
                  className={`et-chip ${daypart === dp ? "et-chip-active" : ""}`}
                >
                  {dp === "am" ? "Morning" : dp === "mid" ? "Midday" : "Late"}
                </button>
              ))}
            </div>

            <div className="mt-3 text-xs text-gray-400">Timezone detected: {tz}</div>

            <div className="mt-3">
              <input 
                value={requestedDateTimeUTC} 
                onChange={(e) => {
                  // FIX #4: Set flag to prevent auto-prefill from overwriting manual edits
                  setSuppressAutoPrefill(true);
                  const val = e.target.value;

                  // FIX #5: Handle empty input gracefully (user clearing the field)
                  if (!val || val.trim() === "") {
                    setRequestedDateTimeUTC("");
                    return;
                  }

                  // FIX #6: CRITICAL - Always store the raw value first to allow typing
                  // Don't validate or auto-correct during onChange - only on blur
                  // This allows the user to type freely without interruption
                  setRequestedDateTimeUTC(val);

                  const m = moment.tz(val, tz);

                  // FIX #7: Only update week chip if we have a complete, valid, future date
                  // Don't do any validation or correction here - just passive observation
                  if (m.isValid() && m.isAfter(moment.tz(tz), "minute")) {
                    // FIX #7a: Auto-sync daypart chip based on selected time
                    const derivedDaypartValue = deriveDaypart(m);
                    setDaypart(derivedDaypartValue);
                  
                    // FIX #8: Only auto-update week chip if user hasn't explicitly selected "later"
                    const derivedWeek = deriveWeek(m, tz);
                    if (week === "later" && derivedWeek !== "later") {
                      // User selected "later" but picked a date in this/next week - respect the date
                      setWeek(derivedWeek);
                    } else if (week !== "later") {
                      // Auto-sync chip for this/next week selections
                      setWeek(derivedWeek);
                    }
                  }
                  // For invalid or past dates during typing, do nothing - let user finish typing
                }}

                onBlur={(e) => {
                  // FIX #9: Validation only happens on blur (when user finishes editing)
                  const val = e.target.value;
                  if (!val) return;

                  const m = moment.tz(val, tz);
                  const now = moment.tz(tz);

                  // FIX #10: If invalid date after user is done typing, reset to sensible default
                  if (!m.isValid()) {
                    const tomorrow = now.clone().add(1, "day").hour(11).minute(0).second(0);
                    setRequestedDateTimeUTC(tomorrow.format("YYYY-MM-DDTHH:mm"));
                    setWeek("this");
                    setDaypart("am"); // Reset daypart to morning
                    return;
                  }

                  // FIX #11: If user entered a past date, auto-correct to next hour
                  if (m.isBefore(now, "minute")) {
                    const corrected = now.clone().add(1, "hour").startOf("minute");
                    setRequestedDateTimeUTC(corrected.format("YYYY-MM-DDTHH:mm"));
                    setWeek(deriveWeek(corrected, tz));
                    setDaypart(deriveDaypart(corrected)); // Sync daypart to corrected time
                    return;
                  }

                  // FIX #12: Valid future date - just sync the week chip if needed
                  const derivedWeek = deriveWeek(m, tz);
                  if (week === "later" && derivedWeek !== "later") {
                    setWeek(derivedWeek);
                  } else if (week !== "later") {
                    setWeek(derivedWeek);
                  }
                  // FIX #12a: Always sync daypart chip to match selected time
                  setDaypart(deriveDaypart(m));
                }}
                type="datetime-local" 
                min={moment().format("YYYY-MM-DDTHH:mm")}
                className="et-input" 
              />

              {/* Dynamic contextual hint */}
              {/* {week !== "later" && requestedDateTimeUTC && confirmationText && ( */}
              {requestedDateTimeUTC && confirmationText && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-3 max-w-[90%]"
                >
                  <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-2 text-xs text-gray-300 leading-relaxed">
                    {confirmationText}
                  </div>
                </motion.div>
              )}

              {week === "later" && !requestedDateTimeUTC && (
                <div className="text-xs text-gray-400 mt-1">
                  Choose any future date and time that works for you.
                </div>
              )}
            </div>
          </UserCard>
        )}

        {showCaptured && i > 6 && requestedDateTimeUTC && (
            <UserResponseCard>
                <p>{formatMeeting(requestedDateTimeUTC)}</p>     
            </UserResponseCard>
        )}

        {/* {i >= 7 && !typing && <BotBubble>All set! I’ll email your Growth Spark and transcript shortly. Want to finish now?</BotBubble>} */}

        {/* 12 Oct 2025 — Step 7 delay before showing final summary */}
        {showCaptured && i === 7 && typing && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-[85%]"
          >
            <div className="flex gap-1 bg-white/5 border border-white/10 rounded-2xl px-4 py-2 text-gray-400">
              <span className="animate-bounce">•</span>
              <span className="animate-bounce delay-150">•</span>
              <span className="animate-bounce delay-300">•</span>
            </div>
          </motion.div>
        )}

        {/* 13 Oct 2025 - Review before confirm */}
        {showCaptured && i === 7 && !typing && (
          <>
            <BotBubble>
              Here's what I’ve captured — review and hit{" "}
              <strong className="text-orange-400">Continue</strong> if it looks good:
            </BotBubble>

            <UserCard>
              <div className="space-y-2 text-sm text-left text-gray-300">
                <div><strong>Name:</strong> {name}</div>
                <div><strong>Email:</strong> {email}</div>
                <div><strong>Phone:</strong> {phone}</div>
                <div><strong>Role:</strong> {role}</div>

                {websiteUrl && <div><strong>Website:</strong> {websiteUrl}</div>}
                {linkedInUrl && <div><strong>LinkedIn:</strong> {linkedInUrl}</div>}
                {youtubeHandle && <div><strong>YouTube:</strong> {youtubeHandle}</div>}
                {organizationName && <div><strong>Org Name:</strong> {organizationName}</div>}

                <div><strong>What's on your mind?:</strong> {growthPrompt}</div>

                {/* <div><strong>Preferred Time:</strong> {moment.tz(requestedDateTimeUTC, tz).format("dddd, MMM D, h:mm A")} ({tz})</div> */}
                <div><strong>Preferred Time:</strong> {formatMeeting(requestedDateTimeUTC)} ({tz})</div>
              </div>
            </UserCard>
          </>
        )}

        {/* 14 Oct 2025 = Option to edit before confirm */}

        {showCaptured && i === 8 && !typing && (
          <>
            <BotBubble>
              Need to tweak anything? You can edit below and then hit{" "}
              <strong className="text-orange-400">Confirm</strong>.
            </BotBubble>

            <UserCard>
              <div className="space-y-3 text-left text-sm text-gray-300">
                {/* Editable fields */}
                <div>
                  <label className="block text-xs text-gray-400">Name</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} className="et-input" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400">Email</label>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} className="et-input" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400">Phone</label>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} className="et-input" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400">Role</label>
                  <input value={role} onChange={(e) => setRole(e.target.value)} className="et-input" />
                </div>
                {/* {organizationName && ( */}
                  <div>
                    <label className="block text-xs text-gray-400">Org Name</label>
                    <input value={organizationName} onChange={(e) => setOrganizationName(e.target.value)} className="et-input" />
                  </div>
                {/* )} */}
                {/* {websiteUrl && ( */}
                  <div>
                    <label className="block text-xs text-gray-400">Website</label>
                    <input value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} className="et-input" />
                  </div>
                {/* )} */}
                {/* {linkedInUrl && ( */}
                  <div>
                    <label className="block text-xs text-gray-400">LinkedIn</label>
                    <input value={linkedInUrl} onChange={(e) => setLinkedInUrl(e.target.value)} className="et-input" />
                  </div>
                {/* )} */}
                {/* {youtubeHandle && ( */}
                  <div>
                    <label className="block text-xs text-gray-400">YouTube</label>
                    <input value={youtubeHandle} onChange={(e) => setYoutubeHandle(e.target.value)} className="et-input" />
                  </div>
                {/* )} */}
                <div>
                  <label className="block text-xs text-gray-400">Growth Spark Prompt</label>
                  <input value={growthPrompt} onChange={(e) => setGrowthPrompt(e.target.value)} className="et-input" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400">Ready to meet:</label>
                  <input 
                  value={requestedDateTimeUTC} 
                  onChange={(e) => setRequestedDateTimeUTC(e.target.value)} 
                  type="datetime-local" 
                  min={moment().format("YYYY-MM-DDTHH:mm")}
                  className="et-input" />
                </div>
              </div>

              <motion.button
                className="mt-4 px-6 py-2 rounded-lg bg-white text-orange-600 font-semibold hover:bg-orange-700 transition"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => {
                  handleNext(); // move to thank-you
                }}
              >
                Confirm
              </motion.button>
            </UserCard>
          </>
        )}

        {i === 9 && !typing && (
          <BotBubble>
            All set! I’ll email your <strong className="text-orange-400">Growth Spark</strong> and transcript shortly.  
            <span className="block text-sm text-gray-400 mt-1">
              Please click the Finish button.
            </span>
          </BotBubble>
        )}

      </div>

      {i > 9 && !typing && (
        <BotBubble>
            <>
                Thanks, <strong>{name}</strong>! I’ll reach out at{" "}
                <span className="text-orange-400">{email || phone}</span> soon
                with your Growth Spark summary.
            </>   
        </BotBubble>
       )}

      {/* Step C: Controls */}
      <div className="mt-4 flex items-center justify-between">
        <button className="text-gray-400 hover:text-white text-sm" onClick={onDone}>Close</button>
        {i < steps.length - 1 ? (
            <motion.button
                className={`px-5 py-2 rounded-lg font-semibold transition ${
                    isStepValid()
                    ? "bg-orange-600 text-white hover:bg-orange-700"
                    : "bg-white/10 text-gray-500 cursor-not-allowed"
                }`}
                whileHover={isStepValid() ? { scale: 1.03 } : {}}
                whileTap={isStepValid() ? { scale: 0.97 } : {}}
                onClick={() => {
                    if (isStepValid()) {

                      if (steps[i] === "presence" && !showOrgField && !websiteUrl.trim() && !linkedInUrl.trim()) {
                        // First click with both empty → reveal org field instead of advancing
                        setShowOrgField(true);
                        return; // stop here, don’t advance yet
                      }
                      //  ✅ Normalize role before advancing
                      if (role === "Other" && otherRole.trim()) {
                        setRole(otherRole.trim());
                        }
                        handleNext();
                    } 
                }}
            >
            Continue
          </motion.button>
        ) : (
          <motion.button
            className={`px-5 py-2 rounded-lg font-semibold flex items-center gap-2 transition ${
              isSubmitting
                ? "bg-orange-800 cursor-wait text-white"
                : "bg-orange-600 hover:bg-orange-700 text-white"
            }`}
            whileHover={!isSubmitting ? { scale: 1.03 } : {}}
            whileTap={!isSubmitting ? { scale: 0.97 } : {}}
            disabled={isSubmitting}
            onClick={handleFinish}
          >
            {isSubmitting && (
              <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            )}
            {isSubmitting ? "Sending..." : "Finish"}
          </motion.button>

          // <motion.button
          //   className="px-5 py-2 rounded-lg bg-orange-600 text-white font-semibold hover:bg-orange-700"
          //   whileHover={{ scale: 1.03 }}
          //   whileTap={{ scale: 0.97 }}
          //   onClick={handleFinish}
          // >
          //   Finish
          // </motion.button>
        )}
      </div>
    </div>
  );
}

export default EtContactConversation

// --- UI atoms ---
function BotBubble({ children }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-[85%]">
      <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-gray-200">{children}</div>
    </motion.div>
  );
}

function UserBubble({ children }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-[80%] ml-auto">
      <div className="bg-orange-600 text-white rounded-2xl px-4 py-2">{children}</div>
    </motion.div>
  );
}

function UserCard({ children }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-[95%] ml-auto">
      <div className="bg-black/40 border border-white/10 rounded-2xl px-4 py-3">{children}</div>
    </motion.div>
  );
}

function UserResponseCard({ children }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-[95%] ml-auto">
      <div 
      className="bg-gradient-to-r from-orange-500 to-orange-600 text-white border border-white/10 rounded-2xl px-4 py-3"
    //   className="bg-orange-600 border border-white/10 rounded-2xl px-4 py-3"
    // className="bg-black/40 border border-white/10 rounded-2xl px-4 py-3"
      >
        {children}
      </div>
    </motion.div>
  );
}


